import { CoinBadgeV2 } from "@components/CoinBadgeV2";
import { CoinDropdownItem } from "@components/dropdown/CoinDropdownItem";
import { CustomDropdown } from "@components/dropdown/CustomDropdown";
import { useAccount } from "@contexts/AccountContext";
import { useCoins } from "@contexts/CoinsContext";
import useAlert from "@hooks/AlertHook";
import { errorPromise } from "@hooks/ApiHook";
import { useTxModal } from "@hooks/TxModalHook";
import { useWasmQuery } from "@hooks/WasmQueryHook";
import { useWasmX } from "@hooks/WasmXHook";
import { CoinResponse, CollectionResponse, NftResponse } from "baaanggg";
import React, { useEffect, useState } from "react";
import { AssetRequest } from "wasmx";

export interface Props {
    nft: NftResponse,
    collection: CollectionResponse
}

export function NftBidItemBox({ nft, collection }: Props) {
    const wasmx = useWasmX()
    const alert = useAlert()
    const txModal = useTxModal()
    const { address } = useAccount()
    const { coins, getCoin } = useCoins()

    const [selectedCoin, setSelectedCoin] = useState<CoinResponse>()
    const [amount, setAmount] = useState("")

    const { data: bidInfo, refresh: refreshBidInfo } = useWasmQuery(wasmx => {
        if (!collection || !address) return errorPromise()
        return wasmx.collection.query.bidByBuyer({
            contract: collection.collectionContract,
            data: {
                buyer: address
            }
        })
    }, [collection, address])

    const onBid = async () => {
        try {
            const parsedAmount = Number.parseFloat(amount)

            if (isNaN(parsedAmount)) {
                alert.failed(`Bid price must be a number greater than 0, use '.' as decimal separator`)
                return
            }

            if (parsedAmount <= 0) {
                alert.failed("Bid price must be greater than 0")
                return
            }

            if (!selectedCoin) throw new Error("Please select coin or token")
            if (!wasmx || !address) throw new Error("Cannot load chain information, try to refresh the page")

            let asset: AssetRequest
            const totalAmount = parsedAmount * Math.pow(10, selectedCoin.decimals)
            if (selectedCoin.isCw20) asset = {
                token: {
                    address: selectedCoin.cw20Address!,
                    amount: totalAmount.toString()
                }
            }
            else asset = {
                coin: {
                    denom: selectedCoin.denom!,
                    amount: totalAmount.toString()
                }
            }

            const bidMsg = wasmx.collection.execute.bid({
                contract: collection.collectionContract,
                sender: address,
                data: {
                    token_id: nft.tokenId
                }
            }, asset)

            await txModal.show(address, [bidMsg], `Bid NFT '${nft.name}'`, {
                onSuccess: async (tx) => {
                    alert.success("Bid sent successfully")
                    await refreshBidInfo()
                },
                onError: (er) => alert.failed(er.message)
            })
        } catch (e) {
            if (e instanceof Error) alert.failed(e.message)
            else console.error(e)
        }
    }

    const onWithdraw = async () => {
        try {
            if (!wasmx || !address) throw new Error("Cannot load chain information, try to refresh the page")

            const withdrawBid = wasmx.collection.execute.withdrawBid({
                contract: collection.collectionContract,
                sender: address,
                data: {
                    token_id: nft.tokenId
                }
            })

            await txModal.show(address, [withdrawBid], `Withdraw bid NFT '${nft.name}'`, {
                onSuccess: async (tx) => {
                    alert.success("Withdraw bid sent successfully")
                    await refreshBidInfo()
                },
                onError: (er) => alert.failed(er.message)
            })
        } catch (e) {
            if (e instanceof Error) alert.failed(e.message)
            else console.error(e)
        }
    }

    const bidExist = () => bidInfo?.bids.some(e => e.token_id == nft.tokenId)

    return <div
        className="rounded-2lg border border-jacarta-100 bg-white p-8 dark:border-gray-400 dark:bg-gray-900"
    >

        {
            !bidExist() && <div className="flex flex-shrink-0 items-center mb-6">
                <div
                    className="flex w-1/2 items-center text-xs font-medium sm:text-sm mr-2">
                    <CustomDropdown
                        value={selectedCoin?.id}
                        format={coin => coin.name}
                        onSelected={coin => setSelectedCoin(coin)}
                        builder={(coin, isSelected) => <CoinDropdownItem coin={coin} isSelected={isSelected} />}
                        items={coins} />
                </div>
                <div
                    className="flex w-full items-center text-xs font-medium sm:text-sm mr-2">
                    <input
                        type="text"
                        value={amount}
                        onChange={({ target }) => setAmount(target.value)}
                        placeholder="100"
                        className="flex h-10 w-full items-center text-white whitespace-nowrap border border-jacarta-100 bg-white p-3 first:rounded-l-lg last:rounded-r-lg focus:ring-jacarta-500 hover:ring-2 hover:ring-jacarta-500/10 placeholder:text-gray-400 dark:border-gray-400 dark:bg-gray-900 sm:px-4 sm:py-2" />
                </div>
            </div>
        }


        {
            bidExist() && <div className="flex flex-wrap">
                {
                    bidInfo!.bids.filter(e => e.token_id == nft.tokenId).map(e => {
                        const coin = getCoin(e.bid_price.coin?.denom ?? e.bid_price.token?.address ?? "")

                        return <div className="mr-2.5 mb-2.5" key={coin?.id}>
                            <CoinBadgeV2 amount={Number(e.bid_price.coin?.amount ?? e.bid_price.token?.amount)} coinId={coin?.id} />
                        </div>
                    })
                }
            </div>
        }

        {
            bidExist() && <button
                onClick={onWithdraw}
                className="inline-block w-full rounded-full bg-jacarta-500 py-3 px-8 text-center font-semibold text-white transition-all hover:bg-jacarta-600">
                Withdraw Bid
            </button>
        }

        {
            !bidExist() && <button
                onClick={onBid}
                className="inline-block w-full rounded-full bg-jacarta-500 py-3 px-8 text-center font-semibold text-white transition-all hover:bg-jacarta-600">
                Bid
            </button>
        }

    </div>
}